import React, { useMemo } from "react";

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}

function toDate(value) {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value) {
  const date = toDate(value);

  if (!date) return "Date unavailable";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function getCommunicationTone(status) {
  const normalizedStatus = normalize(status);

  if (
    ["sent", "delivered", "completed", "success"].includes(
      normalizedStatus
    )
  ) {
    return {
      color: "#166534",
      background: "#dcfce7",
    };
  }

  if (
    ["failed", "error", "undelivered"].includes(
      normalizedStatus
    )
  ) {
    return {
      color: "#991b1b",
      background: "#fee2e2",
    };
  }

  return {
    color: "#1d4ed8",
    background: "#dbeafe",
  };
}

export default function Shift360Communications({
  shift,
  communications = [],
}) {
  const shiftId = String(
    shift?.id || shift?.shiftId || ""
  ).trim();

  const shiftCommunications = useMemo(() => {
    if (!shiftId) return [];

    return (Array.isArray(communications)
      ? communications
      : []
    )
      .filter((communication) => {
        const communicationShiftId = String(
          communication.shiftId ||
            communication.relatedShiftId ||
            communication.entityId ||
            ""
        ).trim();

        return communicationShiftId === shiftId;
      })
      .sort((first, second) => {
        const firstDate = toDate(
          first.createdAt ||
            first.sentAt ||
            first.updatedAt
        );

        const secondDate = toDate(
          second.createdAt ||
            second.sentAt ||
            second.updatedAt
        );

        return (
          (secondDate?.getTime() || 0) -
          (firstDate?.getTime() || 0)
        );
      });
  }, [communications, shiftId]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Communication History
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          Messages and operational notices connected to
          this shift.
        </div>
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 14,
          border: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            color: "#64748b",
            fontSize: 10,
            fontWeight: 900,
            textTransform: "uppercase",
          }}
        >
          Recorded Communications
        </div>

        <div
          style={{
            marginTop: 5,
            color: "#0f172a",
            fontSize: 22,
            fontWeight: 950,
          }}
        >
          {shiftCommunications.length}
        </div>
      </div>

      {shiftCommunications.length === 0 ? (
        <div
          style={{
            padding: 22,
            borderRadius: 14,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            fontWeight: 750,
          }}
        >
          No communications are connected to this shift.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {shiftCommunications.map(
            (communication, index) => {
              const status =
                communication.deliveryStatus ||
                communication.status ||
                "recorded";

              const tone =
                getCommunicationTone(status);

              const channel =
                communication.channel ||
                communication.type ||
                communication.communicationType ||
                "message";

              const recipient =
                communication.recipientName ||
                communication.toName ||
                communication.workerName ||
                communication.recipient ||
                communication.to ||
                "";

              const message =
                communication.message ||
                communication.body ||
                communication.content ||
                communication.subject ||
                "Communication recorded.";

              return (
                <div
                  key={
                    communication.id ||
                    communication.communicationId ||
                    `${shiftId}-${index}`
                  }
                  style={{
                    padding: 14,
                    borderRadius: 14,
                    border: "1px solid #e2e8f0",
                    background: "#ffffff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#0f172a",
                          fontSize: 13,
                          fontWeight: 950,
                        }}
                      >
                        {titleCase(channel)}
                        {recipient
                          ? ` · ${recipient}`
                          : ""}
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          color: "#64748b",
                          fontSize: 11,
                          fontWeight: 750,
                        }}
                      >
                        {formatDateTime(
                          communication.sentAt ||
                            communication.createdAt ||
                            communication.updatedAt
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        padding: "6px 9px",
                        borderRadius: 999,
                        color: tone.color,
                        background: tone.background,
                        fontSize: 10,
                        fontWeight: 950,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {titleCase(status)}
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 10,
                      color: "#475569",
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {String(message)}
                  </div>

                  {communication.createdByName ||
                  communication.createdByEmail ||
                  communication.createdByRole ? (
                    <div
                      style={{
                        marginTop: 9,
                        color: "#94a3b8",
                        fontSize: 10,
                        fontWeight: 750,
                      }}
                    >
                      Recorded by{" "}
                      {communication.createdByName ||
                        communication.createdByEmail ||
                        communication.createdByRole}
                    </div>
                  ) : null}
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}